/**
 * DriverPortalLayout — full-bleed shell for `/driver-portal/*`.
 *
 * Ported from `screens/src/layouts/DriverLayout.jsx`. The driver portal opts
 * OUT of the operator `StaffLayout` chrome (no left-rail of all modules) and
 * renders its own dedicated top-bar + side-rail focused on the driver's own
 * day. The driver's identity comes from the JWT `sub` claim, decoded once at
 * the layout level and passed down to children via `useOutletContext`.
 *
 * Field actions (scan, POD, panic, navigation) deliberately stay on the
 * mobile-driver RN app — the web portal is for review only.
 */
import { type JSX, useMemo, useState } from 'react';
import {
  Link,
  NavLink,
  Outlet,
  useLocation,
  useNavigate,
  useOutletContext,
} from 'react-router-dom';
import {
  ArrowRight,
  BarChart3,
  Bell,
  BookOpen,
  Calendar,
  ChevronDown,
  LayoutDashboard,
  LifeBuoy,
  LogOut,
  type LucideIcon,
  Menu,
  Radio,
  ShieldAlert,
  Smartphone,
  Truck,
  UserCircle2,
  Wallet,
  X,
} from 'lucide-react';

import { decodeStaffClaims } from '../../lib/auth/jwt';
import { useAuth } from '../../lib/auth/useAuth';

interface NavLinkDef {
  readonly name: string;
  readonly path: string;
  readonly icon: LucideIcon;
  readonly end?: boolean;
}

const NAV_LINKS: readonly NavLinkDef[] = [
  { name: 'Today',         path: '/driver-portal',               icon: LayoutDashboard, end: true },
  { name: 'Schedule',      path: '/driver-portal/schedule',      icon: Calendar },
  { name: 'Deliveries',    path: '/driver-portal/deliveries',    icon: Truck },
  { name: 'Scorecard',     path: '/driver-portal/scorecard',     icon: BarChart3 },
  { name: 'Earnings',      path: '/driver-portal/earnings',      icon: Wallet },
  { name: 'Notifications', path: '/driver-portal/notifications', icon: Bell },
  { name: 'Safety',        path: '/driver-portal/safety',        icon: ShieldAlert },
  { name: 'Training',      path: '/driver-portal/training',      icon: BookOpen },
  { name: 'Help',          path: '/driver-portal/help',          icon: LifeBuoy },
  { name: 'Profile',       path: '/driver-portal/profile',       icon: UserCircle2 },
];

export interface DriverPortalContext {
  /** Stable driver identifier — JWT `sub` claim. */
  readonly driverId: string;
  /** Best-effort display name. */
  readonly displayName: string;
  /** Two-letter initials for the avatar circle. */
  readonly initials: string;
}

function initialsFor(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'D';
  if (parts.length === 1) return (parts[0]?.[0] ?? 'D').toUpperCase();
  const first = parts[0]?.[0] ?? '';
  const last = parts[parts.length - 1]?.[0] ?? '';
  return `${first}${last}`.toUpperCase();
}

/** Children call this to read the driver context the layout exposes. */
export function useDriverPortalContext(): DriverPortalContext {
  return useOutletContext<DriverPortalContext>();
}

export function DriverPortalLayout(): JSX.Element {
  const { token, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);

  const claims = useMemo(() => decodeStaffClaims(token), [token]);
  const driverId = claims?.subject ?? 'unknown-driver';
  const displayName = claims?.displayName?.trim().length ? claims.displayName : 'Driver';
  const shortName = displayName.split(/\s+/)[0] ?? 'Driver';
  const initials = initialsFor(displayName);

  // Mock: in production this comes from a live shift query (service-delivery
  // GET /v1/delivery/shifts/me). Until that ships, we render the on-duty
  // banner conditionally based on URL hash so the design walkthrough stays
  // useful — see the comment in DriverOverviewPage.
  const onDuty = true;

  function handleSignOut(): void {
    setDropdownOpen(false);
    logout();
    navigate('/auth/login', { replace: true });
  }

  const isActive = (link: NavLinkDef): boolean => {
    if (link.end) return location.pathname === link.path;
    return location.pathname.startsWith(link.path);
  };

  const context: DriverPortalContext = { driverId, displayName, initials };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-white border-b border-border shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Link to="/driver-portal" className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-nipost-green flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-sm">N</span>
                </div>
                <div className="leading-none border-l border-gray-200 pl-3">
                  <span className="block text-[10px] uppercase tracking-[0.2em] text-gray-400 font-medium">
                    NIPOST
                  </span>
                  <span className="block text-sm font-bold text-gray-900 mt-0.5">
                    Driver Portal
                  </span>
                </div>
              </Link>
            </div>

            <div className="flex items-center gap-3">
              {onDuty ? (
                <span className="hidden sm:inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest bg-success-light text-success px-2.5 py-1 rounded-full">
                  <span className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" />
                  On duty
                </span>
              ) : null}

              <Link
                to="/driver-portal/notifications"
                className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors"
                title="Notifications"
                aria-label="Notifications"
              >
                <Bell className="w-5 h-5 text-gray-600" />
                <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-danger" />
              </Link>

              <Link
                to="/"
                className="hidden sm:inline-flex items-center gap-1.5 text-xs font-medium text-muted hover:text-nipost-green border border-border rounded-lg px-3 py-1.5"
                title="Back to operator console"
              >
                Operator console
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>

              <div className="relative">
                <button
                  type="button"
                  onClick={() => setDropdownOpen((v) => !v)}
                  className="flex items-center gap-2 pl-3 border-l border-border hover:bg-gray-50 rounded-lg px-2 py-1.5 transition-colors"
                  aria-haspopup="menu"
                  aria-expanded={dropdownOpen}
                >
                  <div className="w-8 h-8 rounded-full bg-nipost-green flex items-center justify-center">
                    <span className="text-white text-xs font-medium">{initials}</span>
                  </div>
                  <span className="hidden sm:block text-sm font-medium text-gray-700">
                    {shortName}
                  </span>
                  <ChevronDown className="w-4 h-4 text-muted" />
                </button>
                {dropdownOpen ? (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setDropdownOpen(false)}
                      aria-hidden="true"
                    />
                    <div
                      role="menu"
                      className="absolute right-0 mt-1 w-56 bg-white rounded-xl border border-border shadow-lg z-20 py-1"
                    >
                      <div className="px-4 py-3 border-b border-border">
                        <p className="text-sm font-semibold text-gray-900">{displayName}</p>
                        <p className="text-[11px] text-muted font-mono mt-0.5">{driverId}</p>
                      </div>
                      <Link
                        to="/driver-portal/profile"
                        onClick={() => setDropdownOpen(false)}
                        className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        role="menuitem"
                      >
                        <UserCircle2 className="w-4 h-4" /> Profile &amp; Banking
                      </Link>
                      <Link
                        to="/driver-portal/help"
                        onClick={() => setDropdownOpen(false)}
                        className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        role="menuitem"
                      >
                        <LifeBuoy className="w-4 h-4" /> Help &amp; Support
                      </Link>
                      <button
                        type="button"
                        onClick={handleSignOut}
                        className="flex items-center gap-2 px-4 py-2 text-sm text-danger hover:bg-gray-50 w-full text-left border-t border-border mt-1"
                        role="menuitem"
                      >
                        <LogOut className="w-4 h-4" /> Sign out
                      </button>
                    </div>
                  </>
                ) : null}
              </div>

              <button
                type="button"
                className="md:hidden p-2 rounded-lg hover:bg-gray-100"
                onClick={() => setMobileMenuOpen((v) => !v)}
                aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              >
                {mobileMenuOpen ? (
                  <X className="w-5 h-5 text-gray-600" />
                ) : (
                  <Menu className="w-5 h-5 text-gray-600" />
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* On-duty banner — drives drivers back to the mobile app for field
            actions; the web portal is review-only. */}
        {onDuty ? (
          <div className="mb-6 bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
            <Smartphone className="w-5 h-5 text-amber-700 mt-0.5 flex-shrink-0" />
            <div className="flex-1 text-sm">
              <p className="font-semibold text-amber-900">
                You&apos;re on duty — field actions happen on the mobile app.
              </p>
              <p className="text-amber-800/90">
                The web portal is for review (schedule, earnings, scorecard, payslips,
                notifications). Scanning, POD capture, panic and navigation are
                mobile-only by design.
              </p>
            </div>
            <span className="hidden sm:inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest bg-white text-amber-700 px-2 py-1 rounded-full flex-shrink-0">
              <Radio className="w-3 h-3" /> GPS streaming
            </span>
          </div>
        ) : null}

        <div className="grid lg:grid-cols-[220px_minmax(0,1fr)] gap-6">
          {/* Left rail */}
          <aside className="hidden md:block">
            <nav
              className="bg-white border border-border rounded-xl p-2 sticky top-20"
              aria-label="Driver portal navigation"
            >
              {NAV_LINKS.map((link) => {
                const Icon = link.icon;
                const active = isActive(link);
                return (
                  <NavLink
                    key={link.path}
                    to={link.path}
                    end={link.end}
                    className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${
                      active
                        ? 'bg-nipost-green-light text-nipost-green font-semibold'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    <Icon
                      className={`w-4 h-4 ${
                        active ? 'text-nipost-green' : 'text-gray-400'
                      }`}
                    />
                    {link.name}
                  </NavLink>
                );
              })}
            </nav>
          </aside>

          {/* Mobile menu */}
          {mobileMenuOpen ? (
            <nav
              className="md:hidden mb-4 bg-white border border-border rounded-xl p-2 grid grid-cols-2 gap-1"
              aria-label="Driver portal navigation (mobile)"
            >
              {NAV_LINKS.map((link) => {
                const Icon = link.icon;
                const active = isActive(link);
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
                      active
                        ? 'bg-nipost-green-light text-nipost-green font-semibold'
                        : 'text-gray-600'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {link.name}
                  </Link>
                );
              })}
            </nav>
          ) : null}

          {/* Page content */}
          <main className="min-w-0">
            <Outlet context={context} />
          </main>
        </div>
      </div>
    </div>
  );
}
